/**
 * Service for handling PDF document operations using PDF.js
 */

import {
  getDocument,
  GlobalWorkerOptions,
  PDFWorker,
} from "pdfjs-dist/legacy/build/pdf.mjs";

// Set the worker source for PDF.js
// Use local worker file - the build script copies pdf.worker.min.mjs and renames to .js
GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// Shared worker instance to prevent multiple worker script loads/initializations
let sharedWorker = null;

/**
 * Loads a PDF document from a URL or ArrayBuffer with optional progress callbacks.
 * @param {string|ArrayBuffer} source - URL string or ArrayBuffer of the PDF document
 * @param {Object} [options]
 * @param {function} [options.onProgress] - Callback (loadedBytes, totalBytes)
 * @param {number} [options.expectedTotal] - Optional known total (content_length)
 * @param {number} [options.chunkSize] - Chunk size in bytes (default: 1048576 = 1MB)
 * @param {Object} [options.initParams] - Additional PDF.js initialization parameters that should merge
 *   with the defaults (e.g. custom HTTP headers or worker overrides). Values here take precedence.
 * @returns {Promise<Object>} PDF.js document instance
 */
export const loadPdfDocument = async (source, options = {}) => {
  try {
    // Accept both URL string and ArrayBuffer
    const isString = typeof source === "string";
    const {
      onProgress,
      expectedTotal,
      chunkSize = 1048576,
      initParams = {},
    } = options;

    // Initialize shared worker if not already done
    if (!sharedWorker) {
      sharedWorker = new PDFWorker({
        name: "IGP-Reader-Worker",
      });
    }

    // Configure PDF.js for progressive loading when using a URL
    const params = isString
      ? {
          url: source,
          rangeChunkSize: chunkSize, // Configurable chunk size (default: 1MB)
          disableAutoFetch: false,
          disableStream: false,
          worker: sharedWorker,
          cMapUrl: "/cmaps/",
          cMapPacked: true,
          standardFontDataUrl: "/standard_fonts/",
          ...initParams,
        }
      : {
          data: source,
          worker: sharedWorker,
          cMapUrl: "/cmaps/",
          cMapPacked: true,
          standardFontDataUrl: "/standard_fonts/",
          ...initParams,
        };

    const loadingTask = getDocument(params);

    // Set up progress callback if provided
    // Note: onProgress is a property we assign, not a method to check
    if (onProgress && loadingTask) {
      loadingTask.onProgress = (progressData) => {
        // progressData: { loaded, total } (total may be 0 if unknown yet)
        const loaded = progressData?.loaded || 0;
        const total = progressData?.total || expectedTotal || 0;
        onProgress(loaded, total);
      };
    }

    const pdf = await loadingTask.promise;

    // Ensure progress callback fires final update if we have expected total
    // PDF.js should handle this, but this ensures 100% is reported
    if (onProgress && expectedTotal && expectedTotal > 0) {
      // Use setTimeout to ensure this fires after PDF.js's final callback
      setTimeout(() => {
        onProgress(expectedTotal, expectedTotal);
      }, 0);
    }

    return pdf;
  } catch (error) {
    // Enhanced error handling for range request issues
    const errorMessage = error?.message || String(error) || "Unknown error";

    // Check for Promise.withResolvers unsupported error
    const isOutdatedBrowser = errorMessage.includes("withResolvers");
    if (isOutdatedBrowser) {
      throw new Error(
        "Your browser or device is not supported. Please update to a newer browser version to view this document.",
      );
    }

    const isRangeError =
      errorMessage.includes("range") ||
      errorMessage.includes("206") ||
      errorMessage.includes("Partial Content") ||
      errorMessage.includes("Invalid range");

    if (isRangeError && typeof source === "string") {
      console.warn(
        "PDF chunk loading error detected. This may indicate that the server doesn't support HTTP Range Requests.",
        "Ensure your backend supports 'Accept-Ranges: bytes' and returns '206 Partial Content' responses.",
      );
    }

    console.error("Error loading PDF document:", error);

    // Re-throw with more context if it's a range request issue
    if (isRangeError) {
      throw new Error(
        `PDF loading failed: Server may not support HTTP Range Requests. ${errorMessage}`,
      );
    }

    throw error;
  }
};

/**
 * Gets a specific page from a PDF document
 * @param {Object} pdfDoc - The PDF.js document instance
 * @param {number} pageNumber - The page number to get (1-based index)
 * @returns {Promise<Object>} PDF.js page object
 */
export const getPdfPage = async (pdfDoc, pageNumber) => {
  try {
    if (!pdfDoc) throw new Error("PDF document not loaded");
    return await pdfDoc.getPage(pageNumber);
  } catch (error) {
    console.error(`Error getting PDF page ${pageNumber}:`, error);
    throw error;
  }
};

/**
 * Renders a PDF page to a canvas
 * @param {Object} page - PDF.js page object
 * @param {HTMLCanvasElement} canvas - Canvas element to render to
 * @param {number} scale - Scale factor for rendering
 * @returns {Promise<Object>} Rendering info
 */
export const renderPage = async (page, canvas, scale = 1.0) => {
  try {
    const viewport = page.getViewport({ scale });
    const context = canvas.getContext("2d");

    // Get device pixel ratio for high-DPI displays
    const dpr = window.devicePixelRatio || 1;

    // Quality multiplier: Always render at minimum 1.5x for better quality
    // This ensures crisp text even when display scale is small
    const MIN_RENDER_SCALE = 1.5;
    const qualityMultiplier =
      scale < MIN_RENDER_SCALE ? MIN_RENDER_SCALE / scale : 1.0;
    const effectiveDPR = dpr * qualityMultiplier;

    // Set canvas dimensions accounting for device pixel ratio and quality multiplier
    // This ensures crisp rendering on high-DPI displays and at small scales
    canvas.width = Math.floor(viewport.width * effectiveDPR);
    canvas.height = Math.floor(viewport.height * effectiveDPR);

    // Set CSS size to viewport size (so canvas displays at correct size)
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    // Scale the context to account for device pixel ratio and quality multiplier
    context.scale(effectiveDPR, effectiveDPR);

    // Enable high-quality image smoothing for crisp rendering
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    const renderContext = {
      canvasContext: context,
      viewport,
    };

    return await page.render(renderContext).promise;
  } catch (error) {
    if (error?.name === "RenderingCancelledException") {
      // Silently ignore cancelled rendering
      throw error;
    }
    console.error("Error rendering PDF page:", error);
    throw error;
  }
};

/**
 * Converts hex color to rgba string with given alpha.
 * @param {string} hex - e.g. "#FFFF00" or "#FF0"
 * @param {number} alpha - 0–1
 * @returns {string} e.g. "rgba(255, 235, 59, 0.4)"
 */
const hexToRgba = (hex, alpha = 0.4) => {
  if (!hex || typeof hex !== "string") {
    return "rgba(255, 235, 59, 0.4)";
  }
  const match = hex.replace(/^#/, "").match(/(.{2})(.{2})(.{2})/);
  if (!match) {
    const short = hex.replace(/^#/, "");
    if (short.length === 3) {
      const r = parseInt(short[0] + short[0], 16);
      const g = parseInt(short[1] + short[1], 16);
      const b = parseInt(short[2] + short[2], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return "rgba(255, 235, 59, 0.4)";
  }
  const r = parseInt(match[1], 16);
  const g = parseInt(match[2], 16);
  const b = parseInt(match[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Roman numeral helpers (moved here to avoid a separate utils file)
export function toRoman(num) {
  if (!num || num <= 0) return "";
  let n = Math.floor(Number(num));
  if (Number.isNaN(n) || n <= 0) return "";
  const map = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let s = "";
  for (const [val, sym] of map) {
    while (n >= val) {
      s += sym;
      n -= val;
    }
  }
  return s.toLowerCase();
}

export function formatPageForDisplay(pageNumber, metadata, endPageNumber = null) {
  const formatSingle = (pNum) => {
    const p = Number(pNum) || 0;
    const firstPageRaw = metadata?.first_page ?? null;
    const firstPage =
      firstPageRaw != null && !Number.isNaN(Number(firstPageRaw))
        ? Number(firstPageRaw)
        : null;

    if (firstPage && firstPage > 1 && p > 0 && p < firstPage) {
      return toRoman(p);
    }

    if (firstPage && firstPage > 1 && p >= firstPage) {
      return String(p - firstPage + 1);
    }

    return String(p);
  };

  const startStr = formatSingle(pageNumber);

  if (endPageNumber != null && Number(endPageNumber) !== Number(pageNumber)) {
    const endStr = formatSingle(endPageNumber);
    if (startStr !== endStr) {
      return `${startStr}-${endStr}`;
    }
  }

  return startStr;
}

/**
 * Generates a thumbnail image for a PDF page, optionally with annotation overlays.
 * @param {Object} pdfDoc - PDF.js document instance
 * @param {number} pageNumber - The page number to generate thumbnail for (1-based index)
 * @param {number} thumbnailScale - Scale factor for thumbnail (default: 0.25)
 * @param {Array<{left: number, top: number, width: number, height: number, color?: string}>} [pageHighlights] - Highlights in scale-1 viewport coordinates
 * @param {Array<{left: number, top: number, width: number, height: number, color?: string}>} [pageUnderlines] - Underlines in scale-1 viewport coordinates
 * @param {Array<{points: Array<{x: number, y: number}>, color?: string, lineWidth?: number}>} [pageDrawings] - Freehand strokes in scale-1 viewport coordinates
 * @returns {Promise<string>} Data URL of the thumbnail image
 */
export const generatePageThumbnail = async (
  pdfDoc,
  pageNumber,
  thumbnailScale = 0.25,
  pageHighlights = [],
  pageUnderlines = [],
  pageDrawings = [],
) => {
  try {
    if (!pdfDoc) throw new Error("PDF document not loaded");
    if (pageNumber < 1) throw new Error("Page number must be >= 1");

    const page = await pdfDoc.getPage(pageNumber);
    const viewport = page.getViewport({ scale: thumbnailScale });

    const dpr =
      typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

    // Create a temporary canvas for the thumbnail
    const canvas = document.createElement("canvas");
    canvas.width = Math.floor(viewport.width * dpr);
    canvas.height = Math.floor(viewport.height * dpr);

    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not get canvas context");

    context.scale(dpr, dpr);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";

    // Render the page to the canvas
    const renderContext = {
      canvasContext: context,
      viewport,
    };

    await page.render(renderContext).promise;

    // To map stored annotation coordinates (which the viewer renders using
    // the page base viewport) to the thumbnail viewport, compute a conversion
    // factor between the thumbnail viewport and the canonical base viewport
    // used by the live viewer (`PDF_CSS_UNITS` = 96/72). This keeps thumbnail
    // overlays in the same relative positions as the live viewer.
    const PDF_CSS_UNITS = 96 / 72;
    const baseViewport = page.getViewport({ scale: PDF_CSS_UNITS });
    const conversion =
      baseViewport.width > 0
        ? viewport.width / baseViewport.width
        : thumbnailScale;

    const renderRects = (rects, drawRect) => {
      if (!Array.isArray(rects) || rects.length === 0) return;
      rects.forEach((rect) => {
        const baseLeft = Number(rect.left ?? 0);
        const baseTop = Number(rect.top ?? 0);
        const baseWidth = Number(rect.width ?? 0);
        const baseHeight = Number(rect.height ?? 0);

        const x = baseLeft * conversion;
        const y = baseTop * conversion;
        const w = baseWidth * conversion;
        const h = baseHeight * conversion;

        if (w <= 0 || h <= 0) return;
        drawRect(rect, x, y, w, h);
      });
    };

    // Draw highlight overlays: same color, opacity (0.4), and multiply blend as page highlighting
    if (Array.isArray(pageHighlights) && pageHighlights.length > 0) {
      const defaultHighlight = "rgba(255, 235, 59, 0.4)"; // match PdfViewer highlight layer
      context.globalCompositeOperation = "multiply";
      renderRects(pageHighlights, (hl, x, y, w, h) => {
        context.fillStyle = hl.color
          ? hexToRgba(hl.color, 0.4)
          : defaultHighlight;
        context.fillRect(x, y, w, h);
      });
      context.globalCompositeOperation = "source-over";
    }

    // Render underlines as compact blue strokes so they remain visible at thumbnail size.
    if (Array.isArray(pageUnderlines) && pageUnderlines.length > 0) {
      context.save();
      context.fillStyle = "#2563eb";
      renderRects(pageUnderlines, (ul, x, y, w, h) => {
        const lineHeight = Math.max(1, Math.min(2, h));
        context.fillRect(x, y + h - lineHeight, w, lineHeight);
      });
      context.restore();
    }

    // Render freehand drawings using the same page-space coordinates as the main viewer.
    if (Array.isArray(pageDrawings) && pageDrawings.length > 0) {
      context.save();
      context.strokeStyle = "#ff0000";
      context.fillStyle = "#ff0000";
      context.lineCap = "round";
      context.lineJoin = "round";

      pageDrawings.forEach((stroke) => {
        if (!stroke?.points || stroke.points.length === 0) return;

        const lineWidth = Math.max(
          0.75,
          ((stroke.lineWidth ?? 1) * thumbnailScale) / 2,
        );
        context.lineWidth = lineWidth;

        if (stroke.points.length === 1) {
          const point = stroke.points[0];
          context.beginPath();
          context.arc(
            point.x * thumbnailScale,
            point.y * thumbnailScale,
            Math.max(1, lineWidth / 2),
            0,
            Math.PI * 2,
          );
          context.fill();
          return;
        }

        context.beginPath();
        context.moveTo(
          stroke.points[0].x * thumbnailScale,
          stroke.points[0].y * thumbnailScale,
        );
        for (let i = 1; i < stroke.points.length; i += 1) {
          const point = stroke.points[i];
          context.lineTo(point.x * thumbnailScale, point.y * thumbnailScale);
        }
        context.stroke();
      });

      context.restore();
    }

    // Convert canvas to data URL
    return canvas.toDataURL("image/png");
  } catch (error) {
    if (error?.name === "RenderingCancelledException") {
      // Silently ignore cancelled rendering
      throw error;
    }
    console.error(`Error generating thumbnail for page ${pageNumber}:`, error);
    throw error;
  }
};

/**
 * Sanitizes a title string by removing null characters and other control characters
 * that can cause rendering issues (like square brackets/tofu) on some systems.
 * @param {string} title
 * @returns {string}
 */
export const sanitizeOutlineTitle = (title) => {
  if (typeof title !== "string") return title;
  // Remove null characters (\0) and control characters (range 00-1F, 7F-9F)
  // These often appear as trailing boxes in some PDF viewers/environments
  return title.replace(/[\u0000-\u001F\u007F-\u009F]/g, "").trim();
};

/**
 * Gets the outline (table of contents) from a PDF document
 * @param {Object} pdfDoc - PDF.js document instance
 * @returns {Promise<Array>} Outline items array
 */
export const getPdfOutline = async (pdfDoc) => {
  try {
    if (!pdfDoc) return [];

    const outline = await pdfDoc.getOutline();
    if (!outline || outline.length === 0) return [];

    // Transform the outline into a more usable format
    const transformedOutline = await Promise.all(
      outline.map(async (item) => {
        const sanitizedTitle = sanitizeOutlineTitle(item.title);
        const pageNumber = await extractPageNumber(pdfDoc, item.dest, item);
        return {
          title: sanitizedTitle,
          pageNumber: pageNumber,
          dest: item.dest || null,
          url: item.url || null,
          unsafeUrl: item.unsafeUrl || null,
          items: item.items
            ? await processOutlineItems(pdfDoc, item.items)
            : [],
        };
      }),
    );

    return transformedOutline;
  } catch (error) {
    console.error("Error getting PDF outline:", error);
    return [];
  }
};

/**
 * Helper function to process outline items recursively
 * @private
 */
const processOutlineItems = async (pdfDoc, items) => {
  if (!items || items.length === 0) return [];

  return Promise.all(
    items.map(async (item) => {
      const sanitizedTitle = sanitizeOutlineTitle(item.title);
      const pageNumber = await extractPageNumber(pdfDoc, item.dest, item);
      return {
        title: sanitizedTitle,
        pageNumber: pageNumber,
        dest: item.dest || null,
        url: item.url || null,
        unsafeUrl: item.unsafeUrl || null,
        items: item.items ? await processOutlineItems(pdfDoc, item.items) : [],
      };
    }),
  );
};

/**
 * Helper function to extract page number from destination
 * @private
 */
const parsePageFromUrlLikeString = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    // Handles values like:
    // - #page=12
    // - /foo/bar.pdf#page=12
    // - https://domain/file.pdf?page=12
    const pageMatch =
      value.match(/[#&?]page=(\d+)/i) ||
      value.match(/[#&?]p=(\d+)/i) ||
      value.match(/^page=(\d+)$/i);
    if (!pageMatch) return null;
    const page = Number(pageMatch[1]);
    return Number.isFinite(page) && page > 0 ? page : null;
  } catch (error) {
    return null;
  }
};

const resolvePageIndexFromDestination = async (pdfDoc, pageRef) => {
  if (pageRef == null) return null;

  // Some PDFs store destination as zero-based page index directly.
  if (typeof pageRef === "number" && Number.isFinite(pageRef)) {
    return pageRef;
  }

  // Most PDFs use a page reference object that must be resolved via getPageIndex.
  try {
    const pageIndex = await pdfDoc.getPageIndex(pageRef);
    if (typeof pageIndex === "number" && pageIndex >= 0) {
      return pageIndex;
    }
  } catch (pageIndexError) {
    // Ignore and fall through to other heuristics.
  }

  // Fallback for chunked/split PDFs:
  // destination references may not be "owned" by this PDF.js document instance,
  // but page object ids (num/gen) can still match.
  const refNum = Number(pageRef?.num);
  const refGen = Number(pageRef?.gen ?? 0);
  if (Number.isFinite(refNum)) {
    try {
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
        const page = await pdfDoc.getPage(pageNum);
        const candidateRef =
          page?.ref || page?._pageInfo?.ref || page?._pageDict?._ref || null;
        const candidateNum = Number(candidateRef?.num);
        const candidateGen = Number(candidateRef?.gen ?? 0);

        if (
          Number.isFinite(candidateNum) &&
          candidateNum === refNum &&
          candidateGen === refGen
        ) {
          return pageNum - 1;
        }
      }
    } catch (refScanError) {
      // Ignore and return null below.
    }
  }

  return null;
};

const extractPageNumber = async (pdfDoc, dest, outlineItem = null) => {
  try {
    // Fast path for URL-only outline items (common for some generators).
    const urlFromItem =
      parsePageFromUrlLikeString(outlineItem?.url) ||
      parsePageFromUrlLikeString(outlineItem?.unsafeUrl);
    if (urlFromItem) return urlFromItem;

    if (!dest) return null;

    // If dest is a string, resolve it first
    let destination;
    if (typeof dest === "string") {
      destination = await pdfDoc.getDestination(dest);
      // Fallback: sometimes destination is stored as a hash/query-like value.
      if (!destination) {
        const pageFromDestString = parsePageFromUrlLikeString(dest);
        if (pageFromDestString) return pageFromDestString;
      }
    } else {
      destination = dest;
    }

    if (Array.isArray(destination) && destination.length > 0) {
      // The first element should be a page reference
      const pageRef = destination[0];
      const pageIndex = await resolvePageIndexFromDestination(pdfDoc, pageRef);
      if (pageIndex != null) return pageIndex + 1;
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Resolve page number for a TOC outline item against a specific PDF.js document.
 * Useful for chunked PDFs where destination resolution may work only in a given chunk.
 * @param {Object} pdfDoc
 * @param {Object} outlineItem
 * @returns {Promise<number|null>} 1-based page number (relative to that document)
 */
export const resolveOutlineItemPageNumber = async (pdfDoc, outlineItem) => {
  if (!pdfDoc || !outlineItem) return null;
  return extractPageNumber(pdfDoc, outlineItem.dest, outlineItem);
};
